import { clsx } from '@appvantageasia/core-utils';
import { ErrorBoundary, type ErrorBoundaryProps } from '@sentry/react';
import { Suspense, useId, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import { handleError } from '@/components/blocks/Error';
import VersionUpdateBanner from '@/components/blocks/VersionUpdateBanner';
import Loader from '@/components/common/Loader';
import type { LayoutSource, NavigateItem } from './types';
import Footer from './Footer';
import Header from './Header';
import Sidebar from './Sidebar';
import { useCreateHeader } from './useHeader';

const beforeCapture: ErrorBoundaryProps['beforeCapture'] = scope => {
    scope.setTag('boundary', 'DashboardLayout');
};

export interface LayoutProps {
    menus: NavigateItem[];
    source: LayoutSource;
}

const BaseLayout = ({ menus, source }: LayoutProps) => {
    const { pathname } = useLocation();
    const headerId = useId();
    const header = useCreateHeader(headerId);
    const [collapsed, setCollapsed] = useState(false);
    const [hasNoSubMenu, setHasNoSubMenu] = useState(false);

    return (
        <div>
            <Header source={source} collapsed={collapsed} setCollapsed={setCollapsed} hasNoSubMenu={hasNoSubMenu} />
            <Sidebar
                collapsed={collapsed}
                setCollapsed={setCollapsed}
                hasNoSubMenu={hasNoSubMenu}
                setHasNoSubMenu={setHasNoSubMenu}
                menus={menus}
            />
            <div
                className={clsx('content-wrapper transition-all duration-150', {
                    'ltr:xl:ml-[300px] rtl:xl:mr-[300px]': !collapsed,
                    'ltr:xl:ml-[72px] rtl:xl:mr-[72px]': collapsed || hasNoSubMenu,
                })}
            >
                <div className={clsx('page-min-height p-4 md:p-6')} style={{ backgroundColor: '#eef1f9' }}>
                    <div id={headerId} />
                    {!/^\/leads\/[a-f0-9]{24}/.test(pathname) && <VersionUpdateBanner />}
                    <ErrorBoundary beforeCapture={beforeCapture} fallback={handleError}>
                        <Suspense fallback={<Loader />}>{header.render(<Outlet />)}</Suspense>
                    </ErrorBoundary>
                </div>
            </div>
            <Footer collapsed={collapsed} />
        </div>
    );
};

export default BaseLayout;
